import { Plus, RefreshCw } from "lucide-react";
import { Link, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { EmptyState, InlineAlert, LoadingRows } from "@/components/admin/feedback";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import {
  ProductUsageMatrix,
  type ProductUsageSegment,
  type ProductUsageWindow,
} from "@/components/admin/product-usage-matrix";
import { ProductUsageSummary } from "@/components/admin/product-usage-summary";
import { Button, buttonVariants } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { apiErrorMessage } from "@/lib/api";
import { useCurrentIdentityQuery, useProductUsageQuery } from "@/lib/queries";
import { can } from "@/lib/permissions";
import { useTimezone } from "@/lib/timezone";

const windows = ["24h", "7d", "30d"] as const;
const segments = ["paid", "trial", "combined"] as const;

export function ProductUsagePage() {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const { data: identity } = useCurrentIdentityQuery();
  const [search, setSearch] = useSearchParams();
  const window = (windows.includes(search.get("window") as ProductUsageWindow)
    ? search.get("window")
    : "7d") as ProductUsageWindow;
  const segment = (segments.includes(search.get("segment") as ProductUsageSegment)
    ? search.get("segment")
    : "paid") as ProductUsageSegment;
  const setFilter = (key: "window" | "segment", value: string) =>
    setSearch({ window, segment, [key]: value });
  const usageQuery = useProductUsageQuery(segment, window);
  const refresh = () => void usageQuery.refetch();
  const loadMore = () => void usageQuery.fetchNextPage({ cancelRefetch: false });
  const snapshots = usageQuery.data?.pages ?? [];
  const snapshot = snapshots[0];
  const products = snapshots.flatMap((page) => page.products);

  return (
    <PageFrame size="fluid">
      <PageHeader
        title={t("usage.title", "Dashboard")}
        actions={can(identity?.roles, "createRecords") ? (
          <Link to="/admin/licenses/new" className={buttonVariants({ variant: "primary" })}>
            <Plus className="size-4" aria-hidden="true" />
            {t("uxTasks.issue", "Issue License")}
          </Link>
        ) : undefined}
      />

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <ToggleGroup
            type="single"
            value={segment}
            onValueChange={(value) => value && setFilter("segment", value)}
            aria-label={t("usage.segmentAria", "Product usage segment")}
          >
            {segments.map((value) => (
              <ToggleGroupItem key={value} value={value}>
                {t(`usage.segments.${value}`, value[0].toUpperCase() + value.slice(1))}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <ToggleGroup
            type="single"
            value={window}
            onValueChange={(value) => value && setFilter("window", value)}
            aria-label={t("usage.windowAria", "Recently Seen Device window")}
          >
            {windows.map((value) => (
              <ToggleGroupItem key={value} value={value}>
                {t(`usage.windows.${value}`, value)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {snapshot ? (
            <p className="text-xs text-muted">
              {t("usage.updatedAt", { generated: formatDateTime(snapshot.generated_at), defaultValue: `Updated ${formatDateTime(snapshot.generated_at)}` })}
            </p>
          ) : null}
          <Button
            variant="outline"
            aria-label={usageQuery.isFetching ? t("common.refreshing", "Refreshing…") : t("usage.refreshAria", "Refresh Product usage snapshot")}
            onClick={refresh}
            disabled={usageQuery.isFetching}
          >
            <RefreshCw className="size-4" aria-hidden="true" />
            {usageQuery.isFetching ? t("common.refreshing", "Refreshing…") : t("common.refresh", "Refresh")}
          </Button>
        </div>
      </div>

      {usageQuery.isPending ? (
        <section className="shrink-0 overflow-hidden border-y border-line">
          <LoadingRows />
        </section>
      ) : null}
      {usageQuery.isError ? (
        <InlineAlert title={t("usage.loadError", "Product usage could not load")} message={apiErrorMessage(usageQuery.error)} onRetry={refresh} />
      ) : null}
      {snapshot ? (
        <>
          <ProductUsageSummary products={products} segment={segment} window={window} hasMore={Boolean(usageQuery.hasNextPage)} />
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-baseline gap-2 text-base font-medium text-ink">
              {t("nav.products", "Products")}
              <span className="font-mono text-xs font-normal text-muted">{products.length}</span>
            </h2>
            <Link to="/admin/licenses?view=expiring" className="inline-flex min-h-9 items-center rounded-sm text-xs text-accent underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus pointer-coarse:min-h-11">
              {t("uxTasks.expiring", "Review expiring Licenses")}
            </Link>
          </div>
          {products.length === 0 ? (
            <EmptyState title={t("usage.emptyTitle", "No Products yet")} message={t("usage.emptyDescription", "Create a Product to see its usage snapshot here.")} />
          ) : (
            <ProductUsageMatrix products={products} segment={segment} window={window} />
          )}
        </>
      ) : null}
      {usageQuery.hasNextPage ? (
        <div className="flex shrink-0 justify-center">
          <Button variant="outline" onClick={loadMore} disabled={usageQuery.isFetching}>
            {usageQuery.isFetchingNextPage ? t("usage.loadingMore", "Loading more…") : t("usage.loadMore", "Load more Products")}
          </Button>
        </div>
      ) : null}

      <section className="flex shrink-0 flex-col gap-2 text-xs sm:flex-row sm:items-start sm:justify-between" aria-labelledby="usage-definition">
        <details className="max-w-3xl">
          <summary id="usage-definition" className="cursor-pointer text-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus pointer-coarse:py-3.5">
            {t("usage.definitionSummary", "How these metrics are counted")}
          </summary>
          <p className="mt-2 leading-5 text-muted">
            {t(
              segment === "combined" ? "usage.combinedDefinition" : "usage.definition",
              segment === "combined"
                ? "Combined includes shared device activity only. Effective Active Licenses and Device capacity are unavailable because Paid and Trial commercial semantics differ."
                : "Effective Active License: Active License inside server UTC validity window. Lifecycle-active Activation: Activation with Active status. Recently Seen Device: lifecycle-active Activation with successful activation or authenticated refresh in selected server UTC window, not application-open time. Lifecycle events use real timestamps.",
            )}
          </p>
          {snapshot ? <p className="mt-2 text-muted">{t("usage.windowStartsAt", { started: formatDateTime(snapshot.window_started_at), defaultValue: `Window starts ${formatDateTime(snapshot.window_started_at)}` })}</p> : null}
        </details>
        {snapshot ? <p className="text-muted">{t("usage.productCount", { count: products.length, defaultValue: `${products.length} Products loaded` })}</p> : null}
      </section>
    </PageFrame>
  );
}
